<template>
  <div ref="view">
    <Header>Student Details</Header>
    <div class="tiled-bar-image"></div>
    <Main>
      <div class="grid grid-cols-4 gap-6">
        <div class="p-4 bg-gray-100 rounded-md h-min text-lg !sticky top-6">
          <div class="inline-block text-2xl font-bold" style="color:rgb(0,128,212);">
            <div class="flex items-center gap-[0.35rem] items-baseline">
              <div>Navigation</div>
              <Spinner
                v-if="displayLoadingStudentDetail"
                :size="6"
              />
            </div>
          </div>
          <div class="space-y-3 mt-3 mb-1">
            <div
              v-for="(heading, index) in headingsArray"
              :key="`${heading.ref}${index}`"
              class="flex items-center gap-1">
              <a
                class="inline-block text-xl !no-underline hover:cursor-pointer hover:!text-blue-600 truncate"
                :class="{
                  '!text-gray-500': currentHeadingRef !== heading.ref,
                  '!text-gray-900': currentHeadingRef == heading.ref
                }"
                @click="scrollToRef(heading.ref)">
                {{ heading.name }}
              </a>
            </div>
          </div>
          <div class="flex flex-col space-y-6 mt-4 mb-1">
            <a
              class="inline-block text-xl text-gray-900 !no-underline hover:cursor-pointer hover:text-blue-600 truncate"
              @click="navigateToViewInRouteList">
              View in Route List
            </a>
          </div>
        </div>
        <div class="col-span-3 space-y-6 mb-6">
          <div class="flex-row bg-gray-100 rounded-md p-3 space-y-6">
            <JumpHeading :ref="headingsArray[0].ref">
              <div class="flex flex-row items-baseline">
                <Spinner
                  v-if="displayLoadingStudentDetail"
                  :size="6"
                />
                <div>
                  {{ fullName }}
                </div>
              </div>
            </JumpHeading>
            <div class="flex flex-col space-y-6 text-lg">
              <div class="grid grid-cols-2 space-y-3">
                <div class="flex items-center gap-3" v-if="canViewCstData">
                  <div class="font-bold">
                    Internal Student ID:
                  </div>
                  <span>{{ internalStudentId }}</span>
                </div>
                <div class="flex items-center gap-3">
                  <div class="font-bold">
                    District Student ID:
                  </div>
                  <span>{{ districtStudentId }}</span>
                </div>
                <div class="flex items-center gap-3">
                  <div class="font-bold">
                    Date of Birth:
                  </div>
                  <span>{{ dateOfBirth }}</span>
                </div>
                <div class="flex items-center gap-3">
                  <div class="font-bold">
                    Gender:
                  </div>
                  <span>{{ gender }}</span>
                </div>
              </div>
              <div class="space-y-3">
                <div class="flex items-center gap-3" v-if="isDistrict">
                  <div class="font-bold">
                    District:
                  </div>
                  <span>{{ `${district.code} -- ${district.name}` }}</span>
                </div>
                <div
                  class="flex items-center gap-3"
                  v-if="requestingDistrict.diff"
                >
                <div class="font-bold">
                    Requesting District
                  </div>
                  <span>{{
                    `${requestingDistrict.code} -- ${requestingDistrict.name}`
                  }}</span>
                </div>
                <div
                  class="flex items-center gap-3"
                  v-if="residentDistrict.diff"
                >
                <div class="font-bold">
                    Resident District
                  </div>
                  <span>{{
                    `${residentDistrict.code} -- ${residentDistrict.name}`
                  }}</span>
                </div>
              </div>
            </div>
            <div class="flex items-center justify-between">
              <JumpHeading ref="contacts">
                <div class="flex flex-row items-baseline">
                  <Spinner
                    v-if="displayLoadingContacts"
                    :size="6"
                  />
                  <div>
                    {{ headingNames['contacts'] }}
                  </div>
                </div>
              </JumpHeading>
              <div class="relative group"
                v-if="showEditContactButton">
                <button
                  type="button"
                  class="rounded-full bg-gray-300 p-1 flex items-center hover:bg-sky-600 transition-colors duration-300"
                  @click="editContact"
                >
                  <div class="rounded-full bg-white h-10 w-10 flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-6 w-6 text-gray-500 hover:text-sky-600 transition-colors duration-300"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                      />
                    </svg>
                  </div>
                </button>
                <div
                  class="absolute left-1/2 transform -translate-x-1/2 bottom-14 bg-gray-200 text-black text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                >
                  Edit contact information
                </div>
              </div>
            </div>
            <div class="divide-y divide-gray-300 border-y border-gray-300">
              <div class="p-2" v-if="contacts.length === 0">None</div>
              <div
                class="text-lg text-gray-900 flex justify-between items-center py-2"
                v-for="(contact, index) in contacts"
                :key="`${contact.name}${index}`"
              >
                <div class="flex items-center gap-4">
                  <div class="bg-gray-200 rounded-md p-2 !shadow">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-6 w-6"
                      fill="rgb(241, 111, 66)"
                      viewBox="0 0 24 24"
                      stroke="rgb(241, 111, 66)"
                      stroke-width="2"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                  <div>
                    <div class="font-bold">
                      {{ contact.name }}
                    </div>
                    <div>
                      {{ contact.relationship }}
                    </div>
                  </div>
                </div>
                <div class="flex gap-3">
                  <a
                    class="!no-underline bg-blue-100 p-2 rounded-md !text-blue-900 hover:!text-blue-900 hover:bg-blue-200 transition-colors flex items-center flex-nowrap gap-1"
                    style="background-color: rgb(121, 229, 229);"
                    v-if="contact.email"
                    target="_blank"
                    rel="noreferrer"
                    :href="`mailto:${contact.email}`">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"
                      />
                      <path
                        d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"
                      />
                    </svg>
                    <span>{{ contact.email }}</span>
                  </a>
                  <a
                    class="!no-underline bg-blue-100 p-2 rounded-md !text-blue-900 hover:!text-blue-900 hover:bg-blue-200 transition-colors flex items-center flex-nowrap gap-1"
                    style="background-color: rgb(121, 229, 229);"
                    v-if="contact.phone && contact.phone.match(/\d/g)"
                    target="_blank"
                    rel="noreferrer"
                    :href="`tel:${contact.phone.match(/\d/g).join('')}`">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"
                      />
                    </svg>
                    <span>{{ contact.phone }}</span>
                  </a>
              </div>
            </div>
          </div>
        </div>
          <div class="flex-row bg-gray-100 rounded-md p-3 space-y-6">
            <JumpHeading ref="approvedSchedules">
              {{ headingNames['approvedSchedules'] }}
            </JumpHeading>
            <HelpText>
              This section shows current and past student transportation. It
              will populate with data on the first day the student begins
              riding.
            </HelpText>
            <StudentInfoHistory :dbId="dbId" />
          </div>
          <div class="flex-row bg-gray-100 rounded-md p-3 space-y-6">
            <div class="flex flex-row">
              <div class="flex-grow">
                <JumpHeading ref="pendingRequests">
                  {{ headingNames['pendingRequests'] }}
                </JumpHeading>
              </div>
              <div class="flex gap-2">
                <RoutingStatusSelector
                  v-if="showStatusSelector"
                  :statusFromServer="currentStudentRequestStatus"
                  :dbId="currentScheduleRequestDbId"
                  @reloadStatus="onReloadStatus"
                />
                <RoutingStatusDisplay
                  :status="currentStudentRequestStatus"
                  :requesterName="currentRequesterName"
                  :routerName="currentStudentRequestRouterName"
                  :createdAt="currentStudentRequestCreatedAt"
                />
              </div>
            </div>
            <HelpText>
              There can only be one pending request at a time. Creating a new
              request will override the previous request.
            </HelpText>
            <StudentRequestHistory
              ref="studentRequestHistory"
              :dbId="dbId"
              @statusChange="onChangeStatus"
              @currentRequestDbId="dbId => {
                this.currentScheduleRequestDbId = dbId
              }"
              @currentRequesterName="currentRequesterName => {
                this.currentRequesterName = currentRequesterName
              }"
            />
          </div>
        </div>
      </div>
      <div class="fixed bottom-3 left-3 group space-y-2">
        <div class="space-y-2"
          :class="{
            hidden: !actionsOpen
          }">
          <button
            type="button"
            class="rounded-full bg-gray-300 p-1 flex items-center hover:bg-gray-200 transition-colors duration-300"
            @click="$router.push({ name: 'NewRequestForm' })">
            <div class="rounded-full bg-white h-12 w-12 flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-7 w-7 text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </div>
            <div class="justify-center items-center px-3 flex-grow hidden group-hover:flex">
              <div class="text-base text-gray-600 uppercase font-bold">
                Add student
              </div>
            </div>
          </button>
          <button
            type="button"
            class="rounded-full bg-gray-300 p-1 flex items-center hover:bg-gray-200 transition-colors duration-300 disabled:!opacity-25"
            @click="changeRequestAction">
            <div class="rounded-full bg-white h-12 w-12 flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-7 w-7 text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                />
              </svg>
            </div>
            <div class="justify-center items-center px-3 flex-grow hidden group-hover:flex">
              <div class="text-base text-gray-600 uppercase font-bold">
                New Change Request
              </div>
            </div>
          </button>
          <button
            v-if="showOneTimeNoRide()"
            type="button"
            class="rounded-full bg-gray-300 p-1 flex items-center hover:bg-gray-200 transition-colors duration-300 disabled:!opacity-25"
            @click="noRideAction">
            <div class="rounded-full bg-white h-12 w-12 flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-7 w-7 text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                />
              </svg>
            </div>
            <div class="justify-center items-center px-3 flex-grow hidden group-hover:flex">
              <div class="text-base text-gray-600 uppercase font-bold">
                One Time No ride
              </div>
            </div>
          </button>
          <button
            v-if="showOneTimeRide()"
            type="button"
            class="rounded-full bg-gray-300 p-1 flex items-center hover:bg-gray-200 transition-colors duration-300 disabled:!opacity-25"
            @click="oneTimeRideAction">
            <div class="rounded-full bg-white h-12 w-12 flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-7 w-7 text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <text
                  x="50%"
                  y="50%"
                  text-anchor="middle"
                  dominant-baseline="central"
                  font-size="15"
                  fill="currentColor"
                  font-weight="normal">
                  1x
                </text>
              </svg>
            </div>
            <div class="justify-center items-center px-3 flex-grow hidden group-hover:flex">
              <div class="text-base text-gray-600 uppercase font-bold">
                One Time Ride
              </div>
            </div>
          </button>
          <Modal size="md">
            <template #button="slotProps">
              <button
                type="button"
                class="rounded-full bg-gray-300 p-1 flex items-center hover:bg-gray-200 transition-colors duration-300 disabled:!opacity-25"
                @click="() => slotProps.openModal()">
                <div class="rounded-full bg-white h-12 w-12 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-7 w-7 text-gray-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
                <div class="justify-center items-center px-3 flex-grow hidden group-hover:flex">
                  <div class="text-base text-gray-600 uppercase font-bold">
                    Cancel Transportation
                  </div>
                </div>
              </button>
            </template>
            <template #title>Transportation Cancellation</template>
            <template #content="slotProps">
              <CancelForm
                @closeModal="() => slotProps.closeModal(false)"
                :studentInfo="student"
              />
            </template>
          </Modal>
          <Modal
            size="full"
            width="max">
            <template #button="slotProps">
              <button
                v-if="showNotes"
                type="button"
                class="rounded-full bg-gray-300 p-1 flex items-center hover:bg-gray-200 transition-colors duration-300 disabled:!opacity-25"
                @click="() => slotProps.openModal()">
                <div class="rounded-full bg-white h-12 w-12 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-7 w-7 text-gray-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </div>
                <div class="justify-center items-center px-3 flex-grow hidden group-hover:flex">
                  <div class="text-base text-gray-600 uppercase font-bold">
                    Notes
                  </div>
                </div>
              </button>
            </template>
            <template #title>Notes for
              {{ `${student.firstName} ${student.lastName}` }}
            </template>
            <template #content>
              <NoteForm :studentInfo="student" />
            </template>
          </Modal>
        </div>
        <button
          type="button"
          class="rounded-full bg-gray-300 p-1 flex items-center hover:bg-gray-200 transition-colors duration-300"
          @click="actionsOpen = !actionsOpen">
          <div class="rounded-full bg-white h-12 w-12 flex items-center justify-center">
            <svg
              v-if="!actionsOpen"
              xmlns="http://www.w3.org/2000/svg"
              class="h-7 w-7 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
            <svg
              v-if="actionsOpen"
              xmlns="http://www.w3.org/2000/svg"
              class="h-7 w-7 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
              />
            </svg>
          </div>
          <div class="justify-center items-center px-3 flex-grow hidden group-hover:flex">
            <div class="text-base text-gray-600 uppercase font-bold">
              {{ `${actionsOpen ? 'Hide' : 'Show'} actions` }}
            </div>
          </div>
        </button>
      </div>
    </Main>
  </div>
</template>

<style scoped>
.tiled-bar-image {
  width: 100%;
  height: 25px;
  background-image: url("@/assets/Bar.png");
  background-repeat: repeat-x;
}
</style>

<script>
import settings from '../services/settings.js'
import user from '../services/user/user.js'

import Header from '../components/Header.vue'
import Main from '../components/Main.vue'
import Modal from '../components/Modal.vue'
import CancelForm from './CancelForm.vue'
import StudentRequestHistory from '../components/StudentHistory/StudentRequestHistory.vue'
import StudentInfoHistory from '../components/StudentHistory/StudentInfoHistory.vue'
import RoutingStatusDisplay from '../components/RoutingStatus/RoutingStatusDisplay.vue'
import RoutingStatusSelector from '../components/RoutingStatus/RoutingStatusSelector.vue'
import JumpHeading from '../components/StudentHistory/common/JumpHeading.vue'
import HelpText from '../components/HelpText.vue'
import NoteForm from '../components/forms/NoteForm.vue'
import Spinner from '../components/Spinner.vue'

import { camelCase, forOwn } from 'lodash'

import { getStudentDetail, getContact } from '../services/api'

import DROPDOWN_LISTS from '../components/DropdownLists.json'

export default {
  components: {
    Header,
    Main,
    CancelForm,
    NoteForm,
    Modal,
    StudentRequestHistory,
    StudentInfoHistory,
    JumpHeading,
    HelpText,
    RoutingStatusDisplay,
    RoutingStatusSelector,
    Spinner,
  },
  data() {
    return {
      dbId: -1,
      actionsOpen: true,
      student: {},
      user: undefined,
      currentHeadingRef: '',
      contacts: [],
      currentStudentRequestStatus: undefined,
      currentRequesterName: '---',
      currentStudentRequestRouterName: undefined,
      currentStudentRequestCreatedAt: undefined,
      currentScheduleRequestDbId: -1,

      displayLoadingStudentDetail: false,
      displayLoadingContacts: false,
    }
  },
  methods: {
    changeRequestAction() {
      settings.requestForm.dbId = this.dbId
      this.$router.push({
        name: 'ChangeRequestForm'
      })
    },
    parseDbId(dbId) {
      this.dbId = parseInt(dbId)
    },
    getStudentData(dbId) {
      this.displayLoadingStudentDetail = true
      getStudentDetail(dbId).then(result => {
        this.displayLoadingStudentDetail = false

        this.student = result.data
        this.$emit('dataLoaded')
      }, error => {
        this.displayLoadingStudentDetail = false
        console.warn(error)
      })
      this.displayLoadingContacts = true
      getContact(dbId).then(result => {
        this.displayLoadingContacts = false
        this.contacts = result.data
      }, error => {
        this.displayLoadingContacts = false
        console.warn(error)
      })
    },
    fetchContact() {
      getContact(this.dbId)
        .then(result => {
          this.contacts = result.data || [];
        })
        .catch(error => {
          console.error("Error fetching contact data:", error);
        });
    },
    editContact() {
      if (this.user.can('edit_student_contact')) {
        if (this.contacts && this.contacts.length > 0) {
          this.$router.push({
            name: 'EditContact',
            params: {
              contact: [...this.contacts],
            },
          });
        } else {
          this.$router.push({
            name: 'EditContact',
            params: {
              contact: [{ name: '', relationship: '', email: '', phone: '' }],
            },
          });
        }
        return true;
      }
    },
    capitalize(string) {
      return string
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    },
    getRefRect(ref) {
      return this.$refs[ref].$el.getBoundingClientRect()
    },
    scrollToRef(ref) {
      const marginOffset = 40 + 56
      const elementRectTop = this.getRefRect(ref).top
      const viewRef = this.$refs.view
      const offsetPosition = elementRectTop + viewRef.scrollTop - marginOffset
      viewRef.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    },
    handleScroll() {
      let headingRef = ''
      const marginOffset = 40 + 56 // 56 is navbar offset
      const topHeading = this.headingsArray[0]
      const bottomHeading = this.headingsArray[this.headingsArray.length - 1]
      if (this.getRefRect(topHeading.ref).top - marginOffset - 2 > 0) {
        this.currentHeadingRef = topHeading.ref
        return
      }
      const viewRef = this.$refs.view
      if (viewRef.clientHeight + viewRef.scrollTop + 2 >= viewRef.scrollHeight) {
        this.currentHeadingRef = bottomHeading.ref
        return
      }
      for (let i = 0; i < this.headingsArray.length; i++) {
        const heading = this.headingsArray[i]
        if (this.getRefRect(heading.ref).top - marginOffset - 2 <= 0) {
          if (i === this.headingsArray.length - 1) {
            this.currentHeadingRef = heading.ref
          }
          headingRef = heading.ref
        } else {
          this.currentHeadingRef = headingRef
          return
        }
      }
    },
    navigateToViewInStudentList() {
      this.$router.push({
        name: 'StudentList',
        query: {
          search: this.student.internalId,
        },
      })
    },
    navigateToViewInRouteList() {
      this.$router.push({
        name: 'RouteList',
        query: {
          search: this.student.internalId,
        },
      })
    },
    showOneTimeRide() {
      return this.user?.can('submit_one_time_ride') && this.student.internalId
    },
    showOneTimeNoRide() {
      return this.user?.can('submit_one_time_no_ride') && this.student.internalId
    },
    noRideAction() {
      this.$router.push({
        name: 'OneTimeNoRide',
        params: {
          student: {
            dbId: this.dbId,
            internalId: this.student.internalId,
            studentId: this.student.studentId,
            firstName: this.student.firstName,
            lastName: this.student.lastName
          }
        }
      })
    },
    oneTimeRideAction() {
      this.$router.push({
        name: 'OneTimeRide',
        params: {
          dbId: this.dbId,
        }
      })
    },
    onChangeStatus(newStatus) {
      this.currentStudentRequestStatus = newStatus.status
      this.currentStudentRequestRouterName = newStatus.routed_by
      if (this.canViewCstData) {
        this.currentStudentRequestCreatedAt = newStatus.created_at
      }
    },
    onReloadStatus() {
      this.$refs.studentRequestHistory.reloadRequestStatus()
    },
    checkUserCanViewPage() {
      if (!this.user.can('view_student')) {
        if (this.user.can('view_student_list')) {
          this.$router.push({
            name: 'StudentList'
          })
        }
        else {
          this.$router.push({
            path: '/'
          })
        }
        return true
      }
    },
  },
  computed: {
    canViewCstData() {
      return this.user?.can('view_internal_cst_data')
    },
    showNotes() {
      return this.user?.can('view_student_notes')
    },
    isDistrict() {
      return true
    },
    showStatusSelector() {
      return this.user?.can('view_student_notification')
    },
    showEditContactButton() {
      return this.user?.can('edit_student_contact')
    },
    fullName() {
      if (this.displayLoadingStudentDetail || this.student.firstName == undefined && this.student.lastName == undefined) {
        return 'Student Full Name'
      }

      if (this.student.middleName) {
        return this.capitalize(
          `${this.student.firstName} ${this.student.middleName} ${this.student.lastName}`
        )
      } else {
        return this.capitalize(
          `${this.student.firstName} ${this.student.lastName}`
        )
      }
    },
    gender() {
      if (this.student.gender) {
        return DROPDOWN_LISTS.genderList[this.student.gender]
      } else {
        return ''
      }
    },
    internalStudentId() {
      if (this.canViewCstData && this.student?.internalId) {
        return this.student.internalId
      }
      return ''
    },
    districtStudentId() {
      if (this.canViewCstData && this.student?.studentId) {
        return this.student.studentId
      }
      return ''
    },
    dateOfBirth() {
      if (this.student.dateOfBirth) {
        return new Date(this.student.dateOfBirth).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          timeZone: 'UTC'
        })
      } else {
        return ''
      }
    },
    district() {
      if (this.student?.district) {
        return {
          code: this.student.district.code,
          name: this.student.district.name
        }
      }
      return {
        code: '',
        name: ''
      }
    },
    residentDistrict() {
      if (this.student?.infoResidentDistrict) {
        return {
          code: this.student.infoResidentDistrict.code,
          name: this.student.infoResidentDistrict.name,
          diff: this.student.infoResidentDistrict.code !== this.district.code
        }
      }
      return {
        code: '',
        name: '',
        diff: false
      }
    },
    requestingDistrict() {
      if (this.student?.infoRequestingDistrict) {
        return {
          code: this.student.infoRequestingDistrict.code,
          name: this.student.infoRequestingDistrict.name,
          diff: this.student.infoRequestingDistrict.code !== this.district.code
        }
      }
      return {
        code: '',
        name: '',
        diff: false
      }
    },
    headingNames() {
      return {
        [camelCase(this.fullName)]: this.fullName,
        contacts: 'Contacts',
        approvedSchedules: 'Transportation Schedule and History',
        pendingRequests: 'Most Recent Request'
      }
    },
    headingsArray() {
      let headingsArray = []
      forOwn(this.headingNames, (value, key) => {
        headingsArray.push({ name: value, ref: key })
      })
      return headingsArray
    }
  },
  watch: {
    '$route.params': function (toParams) {
      this.parseDbId(toParams.dbId)
    },
    dbId: async function (newValue) {
      await user.ready()
      this.getStudentData(newValue)
    },
    fullName: function (newValue) {
      this.currentHeadingRef = camelCase(newValue)
    }
  },
  async mounted() {
    this.user = await user.get()
    if (this.checkUserCanViewPage()) {
      return
    }

    this.$refs.view.addEventListener('scroll', this.handleScroll)
  },
  beforeDestroy() {
    this.$refs.view.removeEventListener('scroll', this.handleScroll)
  },
  created() {
    this.parseDbId(this.$route.params.dbId)
  }
}
</script>